import * as React from 'react';
import type { PiletApi } from '@chess/platform-web';
import { LOG_API, type Log } from '@chess/api-log';
import { STORAGE_API, type Storage } from '@chess/api-storage';
import { REVIEW_API, type Review, type ReviewState } from '@chess/api-review';
import { soundFor } from './sound-of.ts';
import { Synth } from './synth.ts';
import { SoundSettingsStore } from './settings.ts';
import { SoundsForm, type SaveStatus } from './SoundsForm.tsx';
import './settings-sounds.css';

// The board's sounds (2026-10-15): one listener on the review, playing a
// sound whenever the position on the board changes, whichever way it was
// changed (a step button or key, a scoresheet click, a dragged piece, the
// main line or a puzzle's solution playing out, a link into the game): one
// move sounds as that move, a jump of several plies as one thud, so Next
// mistake is one sound and the main line one per move (`sound-of.ts` has
// the rule). The puzzle's verdicts chime from its events. Synthesized
// (`synth.ts`), no files. Off with the switch in Settings
// (`settings/sounds.json`, on by default). No UI on the review itself.
export function setup(app: PiletApi) {
  const log = app.getApi<Log>(LOG_API).then((l) => l.child(app.meta.name));
  const storage = app.getApi<Storage>(STORAGE_API).then((s) => s.scope('settings'));
  const settings = new SoundSettingsStore(storage);
  void settings.load();
  const synth = new Synth(() => new AudioContext(), (message) => void log.then((l) => l.warn(message)));
  const play = (sound: Parameters<Synth['play']>[0]) => {
    if (settings.get().enabled && !document.hidden) synth.play(sound);
  };

  // Audio may start only after a gesture: the first click or key on the page
  // makes the context (then every later sound, a timer's included, may play).
  const unlock = () => synth.unlock();
  document.addEventListener('pointerdown', unlock, { capture: true, passive: true });
  document.addEventListener('keydown', unlock, { capture: true, passive: true });

  void app.getApi<Review>(REVIEW_API).then((review) => {
    let prev: ReviewState | undefined = review.state();
    review.subscribe((next) => {
      const sound = soundFor(prev, next);
      prev = next;
      if (sound) play(sound);
    });
  });
  app.on('review.puzzleSolved', () => play('solved'));
  app.on('review.puzzleFailed', () => play('failed'));

  const Section: React.FC = () => {
    const [enabled, setEnabled] = React.useState<boolean | undefined>();
    const [status, setStatus] = React.useState<SaveStatus>('loading');
    const [error, setError] = React.useState<string | undefined>();
    React.useEffect(() => {
      let live = true;
      void settings.load().then((s) => {
        if (!live) return;
        setEnabled(s.enabled);
        setStatus('idle');
      });
      const stop = settings.subscribe((s) => live && setEnabled(s.enabled));
      return () => {
        live = false;
        stop();
      };
    }, []);
    const onChange = (next: boolean) => {
      setStatus('saving');
      setError(undefined);
      settings.save({ enabled: next }).then(
        () => setStatus('saved'),
        (e) => {
          setStatus('error');
          setError(e instanceof Error ? e.message : String(e));
        },
      );
    };
    return <SoundsForm enabled={enabled} status={status} error={error} onChange={onChange} onTry={() => synth.play('capture')} />;
  };
  app.registerExtension('settings.sections', Section, { order: 25 });
}
